import { mhvillageApi } from "api/mhvillage";
import { metaAdsApi } from "api/meta-ads";
import { QUERY_META_ADS_STATUS } from "views/settings-integration/integrations/meta-ads/constants";
import { boomFinanceApi } from "api/boom-finance";
import { Fragment, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  Radio,
  Show
} from "shared/components";
import { Banner } from "shared/components/Banner";
import { Card } from "shared/components/Card";
import GoogleAdsIcon from "shared/icons/brands/GoogleAds-brand-icon.svg?react";
import GoogleAnalyticsIcon from "shared/icons/brands/GoogleAnalytics-brand-icon.svg?react";
import HubSpotIcon from "shared/icons/brands/hubspot.svg?react";
import { cx } from "shared/utils";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  IntegrationId,
  Integration,
  Step,
  DIRECT_CONNECT_INTEGRATIONS,
  SYNDICATION_FEED_BY_INTEGRATION
} from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { useSettingsController } from "views/settings-integration/controller";
import { PMSNames } from "views/settings-integration/integrations/pms/constants";
import { PropertyManagementSystem } from "shared/types/partner";
import { FEATURES, Partner } from "types/landlord/partner";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  InformationCircleIcon,
  SquareLock02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  ULOOP_PRICE_FEATURED,
  ULOOP_PRICE_LEAD
} from "views/settings-integration/integrations/shared/constants";
import { LockedPaywallPopover } from "shared/app/BoomCrmPaywall/LockedPaywallPopover";

interface FirstStepProps {
  selectedIntegration: PropertyManagementSystem | IntegrationId;
  setSelectedIntegration: (
    id: PropertyManagementSystem | IntegrationId
  ) => void;
  setCurrentStep: (step: Step) => void;
  closeModal: () => void;
  uloopAcknowledged: boolean;
  setUloopAcknowledged: (acknowledged: boolean) => void;
}

const BASE_INTEGRATIONS: Integration[] = [
  {
    id: IntegrationId.GA,
    name: "Google Analytics",
    icon: <GoogleAnalyticsIcon />
  },
  {
    id: IntegrationId.GOOGLE_ADS,
    name: "Google Ads",
    icon: <GoogleAdsIcon />
  },
  {
    id: IntegrationId.HUBSPOT,
    name: "HubSpot",
    icon: <HubSpotIcon />
  }
];

const PMS_INTEGRATIONS: Integration[] = [
  {
    id: PropertyManagementSystem.ManageAmerica,
    name: PMSNames[PropertyManagementSystem.ManageAmerica],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.ManageAmerica}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Buildium,
    name: PMSNames[PropertyManagementSystem.Buildium],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Buildium} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Appfolio,
    name: PMSNames[PropertyManagementSystem.Appfolio],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Appfolio} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Yardi,
    name: PMSNames[PropertyManagementSystem.Yardi],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Yardi} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.RentManager,
    name: PMSNames[PropertyManagementSystem.RentManager],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.RentManager}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Resman,
    name: PMSNames[PropertyManagementSystem.Resman],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Resman} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Rentvine,
    name: PMSNames[PropertyManagementSystem.Rentvine],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Rentvine} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Quext,
    name: PMSNames[PropertyManagementSystem.Quext],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Quext} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Entrata,
    name: PMSNames[PropertyManagementSystem.Entrata],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Entrata} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.RentersWarehouse,
    name: PMSNames[PropertyManagementSystem.RentersWarehouse],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.RentersWarehouse}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Propertyware,
    name: PMSNames[PropertyManagementSystem.Propertyware],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.Propertyware}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Revela,
    name: PMSNames[PropertyManagementSystem.Revela],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Revela} size="BASE" />
    )
  }
];

type PmsCapability = {
  requiresBoomScreen: boolean;
  requiresBoomReport: boolean; // boom_report maps to isRentReporting in portal
};

// Maps each PMS to the product features that grant access to it.
// boom_screen = FEATURES.BOOM_SCREEN, boom_report = FEATURES.RENT_REPORTING
const PMS_CAPABILITIES: Record<string, PmsCapability> = {
  // boom_screen only
  [PropertyManagementSystem.Propertyware]: {
    requiresBoomScreen: true,
    requiresBoomReport: false
  },
  // boom_report only
  [PropertyManagementSystem.Resman]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Entrata]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Quext]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  // both boom_screen and boom_report grant access
  [PropertyManagementSystem.Buildium]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.RentManager]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Yardi]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Appfolio]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Rentvine]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.RentersWarehouse]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.ManageAmerica]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  // boom_report only
  [PropertyManagementSystem.Revela]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  }
};

const filterPmsIntegrationsByProducts = (
  integrations: Integration[],
  {
    isBoomScreen,
    isBoomReport
  }: { isBoomScreen: boolean; isBoomReport: boolean }
): Integration[] =>
  integrations.filter((integration) => {
    const cap = PMS_CAPABILITIES[integration.id];
    if (!cap) return false;
    return (
      (cap.requiresBoomScreen && isBoomScreen) ||
      (cap.requiresBoomReport && isBoomReport)
    );
  });

const IntegrationRow = ({
  integration,
  selectedIntegration,
  disabled,
  onSelect,
  className
}: {
  integration: Integration;
  selectedIntegration: string;
  disabled: boolean;
  onSelect: () => void;
  className?: string;
}) => {
  const locked = Boolean(integration.lockedPaywall);
  const row = (
    <Card
      className={cx(
        "p-6 flex justify-between m-1",
        (disabled || locked) && "opacity-50 bg-alpha-40",
        className
      )}
      onClick={() => {
        if (!disabled && !locked) onSelect();
      }}
    >
      <div className="flex items-center gap-4">
        {integration.icon}
        {integration.paid && !locked ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <p className="font-bold text-base">{integration.name}</p>
              <Badge variant={BadgeVariant.WARNING} size={BadgeSize.SM}>
                Paid
              </Badge>
            </div>
            <p className="text-sm text-content-400">{integration.paid.price}</p>
          </div>
        ) : (
          <p className="font-bold text-base">{integration.name}</p>
        )}
      </div>
      {locked ? (
        <HugeiconsIcon
          icon={SquareLock02Icon}
          size={20}
          className="mr-4 self-center text-content-400"
          aria-hidden="true"
        />
      ) : (
        <Radio
          className="mr-4 self-center cursor-pointer"
          id={integration.id}
          name={integration.name}
          onChange={() => null}
          checked={selectedIntegration === integration.id}
        />
      )}
    </Card>
  );

  if (!integration.lockedPaywall) return row;

  return (
    <LockedPaywallPopover feature={integration.lockedPaywall}>
      {row}
    </LockedPaywallPopover>
  );
};

const revealOnMount = (el: HTMLDivElement | null) =>
  el?.scrollIntoView({ block: "end" });

const UloopPaidNotice = ({
  acknowledged,
  onAcknowledgedChange
}: {
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
}) => (
  <div
    ref={revealOnMount}
    className="m-1 space-y-4 rounded-xl border border-alpha-100 bg-background-1000 p-4"
  >
    <Banner
      variant="warning"
      HugeIcon={
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={20}
          className="text-warning-600"
        />
      }
      title="Uloop is a paid syndication feed"
      body={
        <div className="space-y-2">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <b>Per-lead:</b> {ULOOP_PRICE_LEAD}
            </li>
            <li>
              <b>Featured:</b> {ULOOP_PRICE_FEATURED} with unlimited leads
            </li>
          </ul>
          <p>
            You set up and pay for this partnership directly with Uloop. Boom
            doesn&apos;t bill you for Uloop.
          </p>
          <p>Boom will connect you with Uloop to set up the partnership.</p>
        </div>
      }
    />
    <Checkbox
      size={CheckboxSize.SM}
      checked={acknowledged}
      onChange={(e) => onAcknowledgedChange(e.target.checked)}
      label="I understand Uloop is a paid feed and I'll be billed by Uloop."
      labelClassName="whitespace-normal text-sm text-content-1000"
    />
  </div>
);

const syndicationPaywall = (
  isSyndication: boolean
): "syndication" | undefined =>
  isSyndication ? undefined : ("syndication" as const);

const lockboxesPaywall = (isLockboxes: boolean): "lockboxes" | undefined =>
  isLockboxes ? undefined : ("lockboxes" as const);

const buildOthersIntegrations = ({
  isBoomCRM,
  isBoomScreen,
  isLockboxes,
  isSyndication,
  partner,
  isMetaAdsConnected,
  isBoomFinanceEnabled,
  isPropertyShieldConnected
}: {
  isBoomCRM: boolean;
  isBoomScreen: boolean;
  isLockboxes: boolean;
  isSyndication: boolean;
  partner: Partner | null | undefined;
  isMetaAdsConnected: boolean;
  isBoomFinanceEnabled: boolean;
  isPropertyShieldConnected: boolean;
}): Integration[] => {
  const optionalIntegrations: { show: boolean; integration: Integration }[] = [
    // Lock vendors need the `lockboxes` entitlement, which the free Core
    // plan grants — so the row only locks for a partner an admin revoked it
    // from. It stays in the list, greyed with the paywall on hover, instead of
    // vanishing (CRM-759). Figma 34:24027 pairs the Codebox hover with the
    // SHOWINGS copy; we name lockboxes instead, because that is the entitlement
    // actually missing here and the partner may well already have showings.
    {
      show: isBoomCRM,
      integration: {
        id: IntegrationId.CODEBOX,
        name: "Codebox",
        icon: <IntegrationLogoIcon id={IntegrationId.CODEBOX} size="BASE" />,
        lockedPaywall: lockboxesPaywall(isLockboxes)
      }
    },
    {
      show: isBoomCRM,
      integration: {
        id: IntegrationId.SENTRILOCK,
        name: "SentriLock",
        icon: <IntegrationLogoIcon id={IntegrationId.SENTRILOCK} size="BASE" />,
        lockedPaywall: lockboxesPaywall(isLockboxes)
      }
    },
    {
      show: isBoomCRM,
      integration: {
        id: IntegrationId.IGLOOHOME,
        name: "Igloohome",
        icon: <IntegrationLogoIcon id={IntegrationId.IGLOOHOME} size="BASE" />,
        lockedPaywall: lockboxesPaywall(isLockboxes)
      }
    },
    {
      show: isBoomCRM,
      integration: {
        id: IntegrationId.RENTLY_LOCKBOXES,
        name: "Rently Lockboxes",
        icon: (
          <IntegrationLogoIcon
            id={IntegrationId.RENTLY_LOCKBOXES}
            size="BASE"
          />
        ),
        lockedPaywall: lockboxesPaywall(isLockboxes)
      }
    },
    // The seven listing feeds need the `syndication` entitlement, granted by Core.
    // Per CRM-908 they stay listed when an admin revokes it — greyed, with the
    // paywall on hover — instead of vanishing (CRM-759 QA).
    {
      show: isBoomCRM && !partner?.zillow_connected,
      integration: {
        id: IntegrationId.ZILLOW,
        name: "Zillow",
        icon: <IntegrationLogoIcon id={IntegrationId.ZILLOW} size="BASE" />,
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !partner?.zumper_connected,
      integration: {
        id: IntegrationId.ZUMPER,
        name: "Zumper",
        icon: <IntegrationLogoIcon id={IntegrationId.ZUMPER} size="BASE" />,
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !partner?.dwellsy_connected,
      integration: {
        id: IntegrationId.DWELLSY,
        name: "Dwellsy",
        icon: <IntegrationLogoIcon id={IntegrationId.DWELLSY} size="BASE" />,
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !partner?.apartments_com_connected,
      integration: {
        id: IntegrationId.APARTMENTS_COM,
        name: "Apartments.com",
        icon: (
          <IntegrationLogoIcon id={IntegrationId.APARTMENTS_COM} size="BASE" />
        ),
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !partner?.mhvillage_connected,
      integration: {
        id: IntegrationId.MHVILLAGE,
        name: "MHVillage",
        icon: <IntegrationLogoIcon id={IntegrationId.MHVILLAGE} size="BASE" />,
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !partner?.affordable_housing_com_connected,
      integration: {
        id: IntegrationId.AFFORDABLE_HOUSING_COM,
        name: "AffordableHousing.com",
        icon: (
          <IntegrationLogoIcon
            id={IntegrationId.AFFORDABLE_HOUSING_COM}
            size="BASE"
          />
        ),
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !partner?.rental_beast_connected,
      integration: {
        id: IntegrationId.RENTAL_BEAST,
        name: "Rental Beast",
        icon: (
          <IntegrationLogoIcon id={IntegrationId.RENTAL_BEAST} size="BASE" />
        ),
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !partner?.uloop_connected,
      integration: {
        id: IntegrationId.ULOOP,
        name: "Uloop",
        icon: <IntegrationLogoIcon id={IntegrationId.ULOOP} size="BASE" />,
        lockedPaywall: syndicationPaywall(isSyndication),
        paid: { price: `${ULOOP_PRICE_LEAD} or ${ULOOP_PRICE_FEATURED}` }
      }
    },
    {
      show: isBoomCRM && !partner?.rental_source_connected,
      integration: {
        id: IntegrationId.RENTAL_SOURCE,
        name: "Rental Source",
        icon: (
          <IntegrationLogoIcon id={IntegrationId.RENTAL_SOURCE} size="BASE" />
        ),
        lockedPaywall: syndicationPaywall(isSyndication)
      }
    },
    {
      show: isBoomCRM && !isMetaAdsConnected,
      integration: {
        id: IntegrationId.META_ADS,
        name: "Meta Lead Ads",
        icon: <IntegrationLogoIcon id={IntegrationId.META_ADS} size="BASE" />
      }
    },
    {
      show: isBoomScreen && isBoomFinanceEnabled,
      integration: {
        id: IntegrationId.BOOM_FINANCE,
        name: "BoomFinance",
        icon: (
          <IntegrationLogoIcon id={IntegrationId.BOOM_FINANCE} size="BASE" />
        )
      }
    },
    {
      show: !isPropertyShieldConnected,
      integration: {
        id: IntegrationId.PROPERTY_SHIELD,
        name: "Property shield",
        icon: (
          <IntegrationLogoIcon id={IntegrationId.PROPERTY_SHIELD} size="BASE" />
        )
      }
    }
  ];

  return [
    ...BASE_INTEGRATIONS,
    ...optionalIntegrations
      .filter((option) => option.show)
      .map((option) => option.integration)
  ].sort((a, b) => a.name.localeCompare(b.name));
};

export const FirstStep = ({
  selectedIntegration,
  setSelectedIntegration,
  setCurrentStep,
  closeModal,
  uloopAcknowledged,
  setUloopAcknowledged
}: FirstStepProps) => {
  const { currentSettings, hubspotHubId } = useSettingsController();
  const { type, partner, refreshPartnerData } = useAuthContext();
  const isBoomFinanceEnabled = partner?.features.includes(
    FEATURES.WIP_BOOM_FINANCE
  );

  const availablePmsIntegrations = useMemo(
    () =>
      filterPmsIntegrationsByProducts(PMS_INTEGRATIONS, {
        isBoomScreen: type.isBoomScreen,
        isBoomReport: type.isRentReporting
      }).sort((a, b) => a.name.localeCompare(b.name)),
    [type.isBoomScreen, type.isRentReporting]
  );

  const { mutate: connectMhvillage, isPending: isConnectingMhvillage } =
    useMutation({
      mutationKey: ["MHVILLAGE", "CREATE"],
      mutationFn: () => mhvillageApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { data: metaAdsStatus } = useQuery({
    queryKey: QUERY_META_ADS_STATUS,
    queryFn: metaAdsApi.getStatus,
    enabled: type.isBoomCRM
  });

  const { mutate: startMetaAdsOauth, isPending: isStartingMetaAdsOauth } =
    useMutation({
      mutationKey: ["META_ADS", "AUTHORIZATION_URL"],
      mutationFn: metaAdsApi.getAuthorizationUrl,
      onSuccess: ({ authorization_url }) => {
        window.location.assign(authorization_url);
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { mutate: connectBoomFinance, isPending: isConnectingBoomFinance } =
    useMutation({
      mutationKey: ["BOOM_FINANCE", "CREATE"],
      mutationFn: () => boomFinanceApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const othersIntegrations = buildOthersIntegrations({
    isBoomCRM: type.isBoomCRM,
    isBoomScreen: type.isBoomScreen,
    isLockboxes: type.isLockboxes,
    isSyndication: type.isSyndication,
    partner,
    isMetaAdsConnected: Boolean(metaAdsStatus?.connected),
    isBoomFinanceEnabled: Boolean(isBoomFinanceEnabled),
    isPropertyShieldConnected: type.isListingFraudAlerts
  });

  const connectActions: Partial<
    Record<PropertyManagementSystem | IntegrationId, () => void>
  > = {
    [IntegrationId.MHVILLAGE]: connectMhvillage,
    [IntegrationId.META_ADS]: startMetaAdsOauth,
    [IntegrationId.BOOM_FINANCE]: connectBoomFinance,
    [IntegrationId.PROPERTY_SHIELD]: () => setCurrentStep(Step.INTEGRATION_NAME)
  };

  const handleContinue = () => {
    const id = selectedIntegration as PropertyManagementSystem | IntegrationId;
    if (SYNDICATION_FEED_BY_INTEGRATION[id as IntegrationId]) {
      setCurrentStep(Step.CHOOSE_LISTINGS);
      return;
    }
    const connectAction = connectActions[id];
    if (connectAction) connectAction();
    else if (DIRECT_CONNECT_INTEGRATIONS.has(id)) return;
    else setCurrentStep(Step.SECOND);
  };

  const selectIntegration = (id: PropertyManagementSystem | IntegrationId) => {
    if (id !== IntegrationId.ULOOP) setUloopAcknowledged(false);
    setSelectedIntegration(id);
  };

  const showUloopNotice = (integration: Integration) =>
    integration.id === IntegrationId.ULOOP &&
    selectedIntegration === IntegrationId.ULOOP &&
    !integration.lockedPaywall;

  const isContinueDisabled =
    !selectedIntegration ||
    (selectedIntegration === IntegrationId.ULOOP && !uloopAcknowledged);

  const uloopNotice = (
    <UloopPaidNotice
      acknowledged={uloopAcknowledged}
      onAcknowledgedChange={setUloopAcknowledged}
    />
  );

  const isDisabled = (id: string) => {
    if (id === IntegrationId.GA)
      return Boolean(currentSettings.google.googleAnalyticsId);
    if (id === IntegrationId.GOOGLE_ADS)
      return Boolean(currentSettings.googleAds.conversionId);
    if (id === IntegrationId.HUBSPOT) return Boolean(hubspotHubId);
    return false;
  };

  if (type.isMultiPMS)
    return (
      <>
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto gap-4">
          {availablePmsIntegrations.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-content-700">
                Property management system
              </p>
              <div className="flex flex-col gap-2">
                {availablePmsIntegrations.map((integration) => (
                  <IntegrationRow
                    key={integration.id}
                    integration={integration}
                    selectedIntegration={selectedIntegration}
                    disabled={false}
                    onSelect={() =>
                      selectIntegration(
                        integration.id as
                          | PropertyManagementSystem
                          | IntegrationId
                      )
                    }
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-content-700">Others</p>
            <div className="flex flex-col gap-2">
              {othersIntegrations.map((integration) => (
                <Fragment key={integration.id}>
                  <IntegrationRow
                    integration={integration}
                    selectedIntegration={selectedIntegration}
                    disabled={isDisabled(integration.id)}
                    onSelect={() =>
                      selectIntegration(
                        integration.id as
                          | PropertyManagementSystem
                          | IntegrationId
                      )
                    }
                  />
                  <Show if={showUloopNotice(integration)}>{uloopNotice}</Show>
                </Fragment>
              ))}
            </div>
          </div>
        </div>

        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={handleContinue}
          disabled={isContinueDisabled}
          loading={
            isConnectingMhvillage ||
            isConnectingBoomFinance ||
            isStartingMetaAdsOauth
          }
          className="ml-auto mt-4"
        >
          Continue
        </Button>
      </>
    );

  // Legacy flow. Shares IntegrationRow with the multi-PMS branch above rather
  // than re-implementing the row: this branch used to have its own copy, which
  // silently skipped the entitlement lock and left a clickable Codebox /
  // SentriLock row for a single-PMS partner without `lockboxes` (CRM-759).
  return (
    <>
      <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
        {othersIntegrations.map((integration) => (
          <Fragment key={integration.id}>
            <IntegrationRow
              integration={integration}
              selectedIntegration={selectedIntegration}
              disabled={isDisabled(integration.id)}
              className="shrink-0"
              onSelect={() =>
                selectIntegration(
                  integration.id as PropertyManagementSystem | IntegrationId
                )
              }
            />
            <Show if={showUloopNotice(integration)}>{uloopNotice}</Show>
          </Fragment>
        ))}
      </div>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={handleContinue}
        disabled={isContinueDisabled}
        loading={
          isConnectingMhvillage ||
          isConnectingBoomFinance ||
          isStartingMetaAdsOauth
        }
        className="ml-auto mt-4"
      >
        Continue
      </Button>
    </>
  );
};
